import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Tooltip,
  Switch,
  FormControlLabel,
  useMediaQuery,
  Fab,
  Zoom,
  Slider,
  Tabs,
  Tab,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Discount,
  Search,
  Edit,
  Delete,
  Add,
  Close,
  CheckCircle,
  Percent,
  Refresh,
  Warning,
  KeyboardArrowUp,
  Save,
  History,
  Person,
  Schedule,
  TrendingDown,
  ToggleOn,
  ToggleOff,
  Block,
  CheckCircleOutline,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import axios from 'axios';
import Sidebar from '../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../components/TopNav_hotelAdmin';

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

// Styled button for deactivate/activate
const ActionButton = styled(Button)(({ theme, active }) => ({
  borderRadius: '20px',
  padding: '6px 16px',
  fontWeight: 'bold',
  textTransform: 'none',
  fontSize: '0.875rem',
  ...(active === 'active' && {
    backgroundColor: '#dc2626',
    color: 'white',
    '&:hover': {
      backgroundColor: '#b91c1c',
    },
  }),
  ...(active === 'inactive' && {
    backgroundColor: '#16a34a',
    color: 'white',
    '&:hover': {
      backgroundColor: '#15803d',
    },
  }),
}));

const DiscountManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [globalDiscount, setGlobalDiscount] = useState(null);
  const [allDiscountHistory, setAllDiscountHistory] = useState([]);
  const [discountStats, setDiscountStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);
  
  const [discountDialog, setDiscountDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [toggleDialog, setToggleDialog] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [toggleAction, setToggleAction] = useState(null);
  
  const [formData, setFormData] = useState({
    discount_percentage: '',
    is_active: true,
    notes: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showScrollButton, setShowScrollButton] = useState(false);

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
  const DISCOUNT_API_PATH = '/hotel/rooms/discount_api.php';
  const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME || 'Hotel';

  const getHotelCode = () => {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
  };

 const getStaffCode = () => {
  const storedProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
  
  // For hotel admin (from eneurons login)
  if (storedProfile.user_type === 'hotel_admin') {
    return storedProfile.id || '';  // Use 'id' field for admin
  }
  
  // For regular staff (from staff login)
  return storedProfile.staff_id || storedProfile.staff_code || storedProfile.staffCode || '';
};

const getStaffName = () => {
  const storedProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
  
  // For hotel admin
  if (storedProfile.user_type === 'hotel_admin') {
    return storedProfile.hotel_name || 'Hotel Admin';
  }
  
  // For regular staff
  return storedProfile.full_name || storedProfile.name || 
    `${storedProfile.first_name || ''} ${storedProfile.other_names || ''}`.trim() || 'Staff';
};

// Add this helper function for debugging
const getUserType = () => {
  const storedProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
  return storedProfile.user_type || '';
};

  const hotelCode = getHotelCode();
  const staffId = getStaffCode();
  const staffName = getStaffName();

  // Create axios instance
  const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    }
  });

  // API calls
  const api = {
    getGlobalDiscount: async () => {
      const response = await apiClient.get(DISCOUNT_API_PATH, {
        params: { 
          action: 'global', 
          hotel_code: hotelCode,
          staff_id: staffId
        }
      });
      return response.data;
    },
    
    getAllDiscountHistory: async () => {
      const response = await apiClient.get(DISCOUNT_API_PATH, {
        params: { 
          action: 'history', 
          hotel_code: hotelCode,
          staff_id: staffId
        }
      });
      return response.data;
    },
    
    getBookingDiscounts: async () => {
      const response = await apiClient.get(DISCOUNT_API_PATH, {
        params: { 
          action: 'booking', 
          hotel_code: hotelCode,
          staff_id: staffId
        }
      });
      return response.data;
    },
    
    getDiscountStats: async () => {
      const response = await apiClient.get(DISCOUNT_API_PATH, {
        params: { 
          action: 'stats', 
          hotel_code: hotelCode,
          staff_id: staffId
        }
      });
      return response.data;
    },
    
    setGlobalDiscount: async (data) => {
      const response = await apiClient.post(DISCOUNT_API_PATH, data, {
        params: { 
          hotel_code: hotelCode,
          staff_id: staffId
        }
      });
      return response.data;
    },
    
    toggleDiscountStatus: async (id, isActive) => {
      const response = await apiClient.put(DISCOUNT_API_PATH, {
        id: id,
        is_active: isActive
      }, {
        params: { 
          hotel_code: hotelCode,
          staff_id: staffId
        }
      });
      return response.data;
    },
    
    removeDiscount: async (id) => {
      const response = await apiClient.delete(DISCOUNT_API_PATH, {
        params: { 
          id: id, 
          hotel_code: hotelCode,
          staff_id: staffId
        }
      });
      return response.data;
    }
  };

  // Load data
  useEffect(() => {
    if (hotelCode && staffId) {
      loadAllData();
    } else {
      showSnackbar('Unable to load: Missing hotel or staff information', 'error');
    }
  }, [hotelCode, staffId]);

  useEffect(() => {
    if (activeTab === 1 && hotelCode) {
      loadBookingDiscounts();
    }
  }, [activeTab, searchQuery]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButton(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadGlobalDiscount(),
        loadAllDiscountHistory(),
        loadDiscountStats(),
        loadBookingDiscounts()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Failed to load data: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadGlobalDiscount = async () => {
    try {
      const response = await api.getGlobalDiscount();
      if (response.success) {
        const discountData = response.data?.discount || response.discount;
        if (discountData && discountData.discount_percentage !== undefined) {
          setGlobalDiscount(discountData);
          setFormData({
            discount_percentage: discountData.discount_percentage,
            is_active: discountData.is_active === 1 || discountData.is_active === true,
            notes: discountData.notes || '',
          });
        } else {
          setGlobalDiscount(null);
        }
      } else {
        setGlobalDiscount(null);
      }
    } catch (error) {
      console.error('Error loading global discount:', error);
      setGlobalDiscount(null);
    }
  };

  const loadAllDiscountHistory = async () => {
    try {
      const response = await api.getAllDiscountHistory();
      if (response.success) {
        const historyData = response.data?.discounts || response.discounts || [];
        setAllDiscountHistory(historyData);
      }
    } catch (error) {
      console.error('Error loading discount history:', error);
    }
  };

  const loadDiscountStats = async () => {
    try {
      const response = await api.getDiscountStats();
      if (response.success) {
        setDiscountStats(response.data);
      }
    } catch (error) {
      console.error('Error loading discount stats:', error);
    }
  };

  const loadBookingDiscounts = async () => {
    try {
      const response = await api.getBookingDiscounts();
      if (response.success) {
        const discountsData = response.data?.discounts || response.discounts || [];
        setDiscounts(discountsData);
      }
    } catch (error) {
      console.error('Error loading discounts:', error);
    }
  };

  const handleInputChange = (field) => (event) => {
    let value = event.target.value;
    if (field === 'discount_percentage') {
      if (value === '') {
        setFormData({ ...formData, [field]: '' });
      } else {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          const clampedValue = Math.min(100, Math.max(0, numValue));
          setFormData({ ...formData, [field]: clampedValue });
        }
      }
    } else {
      setFormData({ ...formData, [field]: value });
    }
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }
  };

  const handleSliderChange = (event, newValue) => {
    setFormData({ ...formData, discount_percentage: newValue });
    if (formErrors.discount_percentage) {
      setFormErrors({ ...formErrors, discount_percentage: '' });
    }
  };

  const handleSwitchChange = (event) => {
    setFormData({ ...formData, is_active: event.target.checked });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.discount_percentage && formData.discount_percentage !== 0) {
      errors.discount_percentage = 'Discount percentage is required';
    } else if (formData.discount_percentage <= 0 || formData.discount_percentage > 100) {
      errors.discount_percentage = 'Discount must be between 1% and 100%';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSetGlobalDiscount = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const response = await api.setGlobalDiscount({
        discount_percentage: formData.discount_percentage,
        is_active: formData.is_active ? 1 : 0,
        notes: formData.notes || `Discount set by ${staffName} (ID: ${staffId})`,
      });
      
      if (response.success) {
        showSnackbar(response.message, 'success');
        setDiscountDialog(false);
        await loadAllData();
        
        setFormData({
          discount_percentage: '',
          is_active: true,
          notes: '',
        });
      } else {
        showSnackbar(response.error || 'Failed to set discount', 'error');
      }
    } catch (error) {
      console.error('Error setting discount:', error);
      showSnackbar(error.response?.data?.error || 'Failed to set discount', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleDiscountStatus = async () => {
    setSubmitting(true);
    try {
      const newStatus = !selectedDiscount.is_active;
      const response = await api.toggleDiscountStatus(selectedDiscount.id, newStatus);
      
      if (response.success) {
        showSnackbar(`Discount ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
        setToggleDialog(false);
        await loadAllData();
      } else {
        showSnackbar(response.error || 'Failed to update discount status', 'error');
      }
    } catch (error) {
      console.error('Error updating discount status:', error);
      showSnackbar('Failed to update discount status', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveDiscount = async () => {
    setSubmitting(true);
    try {
      const response = await api.removeDiscount(selectedDiscount.id);
      if (response.success) {
        showSnackbar(response.message, 'success');
        setDeleteDialog(false);
        await loadAllData();
      } else {
        showSnackbar(response.error || 'Failed to remove discount', 'error');
      }
    } catch (error) {
      console.error('Error removing discount:', error);
      showSnackbar('Failed to remove discount', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleClick = (discount) => {
    setSelectedDiscount(discount);
    setToggleAction(discount.is_active ? 'deactivate' : 'activate');
    setToggleDialog(true);
  };

  const handleDeleteClick = (discount) => {
    setSelectedDiscount(discount);
    setDeleteDialog(true);
  };

  const filteredDiscounts = discounts.filter(discount =>
    discount.booking_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    discount.guest_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleRefresh = () => {
    loadAllData();
    showSnackbar('Data refreshed!', 'success');
  };

  const toggleSidebar = () => setSidebarOpen(prev => !prev);
  
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    window.location.href = '/Entri';
  };

  const handleNotificationClick = () => {
    showSnackbar('You have new notifications!', 'info');
  };

  // ========== RENDER FUNCTIONS ==========
  const renderStatsCards = () => {
    if (!discountStats) return null;
    
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Total Discounts
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {discountStats.total_discounts || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                  <Discount />
                </Avatar>
              </Box>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Active Global Discount
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {discountStats.active_global || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Applied to Bookings
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {discountStats.applied_to_bookings || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main', width: 48, height: 48 }}>
                  <TrendingDown />
                </Avatar>
              </Box>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Avg Discount %
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {discountStats.avg_discount || 0}%
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 48, height: 48 }}>
                  <Percent />
                </Avatar>
              </Box>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>
    );
  };

  const renderDiscountSettings = () => (
    <Card sx={{ mb: 4, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Discount color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Global Room Discount
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={globalDiscount ? <Edit /> : <Add />}
            onClick={() => setDiscountDialog(true)}
            size="large"
          >
            {globalDiscount ? 'Update Discount' : 'Set Discount'}
          </Button>
        </Box>
        
        {globalDiscount ? (
          <Alert severity={globalDiscount.is_active ? 'success' : 'warning'} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="body2">
                  <strong>Current Discount:</strong> {globalDiscount.discount_percentage}% off all rooms
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {globalDiscount.is_active ? 'Active' : 'Inactive'}
                </Typography>
                <Typography variant="body2">
                  <strong>Set by:</strong> {globalDiscount.set_by} on {globalDiscount.set_date}
                </Typography>
               
              </Box>
              <ActionButton
                variant="contained"
                active={globalDiscount.is_active ? 'active' : 'inactive'}
                onClick={() => handleToggleClick(globalDiscount)}
                startIcon={globalDiscount.is_active ? <Block /> : <CheckCircleOutline />}
                size="medium"
              >
                {globalDiscount.is_active ? 'DEACTIVATE DISCOUNT' : 'ACTIVATE DISCOUNT'}
              </ActionButton>
            </Box>
          </Alert>
        ) : (
          <Alert severity="info">
            No active global discount. Click "Set Discount" to apply a discount to all room bookings.
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  // Render full discount history with all previous discounts
  const renderFullDiscountHistory = () => (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Complete Discount History
            </Typography>
          </Box>
          <Chip 
            label={`${allDiscountHistory.length} Total Discounts`} 
            color="primary" 
            size="medium"
          />
        </Box>
        
        {allDiscountHistory.length === 0 ? (
          <Alert severity="info">No discount history available</Alert>
        ) : (
          <>
            <TableContainer component={Paper} elevation={1}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Discount %</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Set By</strong></TableCell>
                    <TableCell><strong>Date Set</strong></TableCell>
                    <TableCell><strong>Notes</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allDiscountHistory
                    .slice(historyPage * historyRowsPerPage, historyPage * historyRowsPerPage + historyRowsPerPage)
                    .map((discount, index) => (
                      <TableRow key={discount.id} hover>
                        <TableCell>{historyPage * historyRowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Chip
                            label={`${discount.discount_percentage}%`}
                            size="medium"
                            color="warning"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={discount.discount_type}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={discount.is_active ? 'Active' : 'Inactive'} 
                            size="small"
                            color={discount.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person fontSize="small" color="action" />
                            <Typography variant="body2">
                              {discount.set_by}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Schedule fontSize="small" color="action" />
                            <Typography variant="body2">
                              {discount.set_date}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {discount.notes || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <ActionButton
                            variant="contained"
                            active={discount.is_active ? 'active' : 'inactive'}
                            onClick={() => handleToggleClick(discount)}
                            startIcon={discount.is_active ? <Block /> : <CheckCircleOutline />}
                            size="small"
                          >
                            {discount.is_active ? 'Deactivate' : 'Activate'}
                          </ActionButton>
                          {discount.booking_number && (
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDeleteClick(discount)}
                              sx={{ ml: 1 }}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={allDiscountHistory.length}
                rowsPerPage={historyRowsPerPage}
                page={historyPage}
                onPageChange={(e, newPage) => setHistoryPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setHistoryRowsPerPage(parseInt(e.target.value, 10));
                  setHistoryPage(0);
                }}
              />
            </TableContainer>
            
            {/* Summary of discount history */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">History Summary:</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Total Global Discounts</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {allDiscountHistory.filter(d => d.discount_type === 'Global Discount').length}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Booking Discounts</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {allDiscountHistory.filter(d => d.discount_type !== 'Global Discount').length}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Currently Active</Typography>
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    {allDiscountHistory.filter(d => d.is_active).length}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Highest Discount</Typography>
                  <Typography variant="body2" fontWeight="bold" color="warning.main">
                    {Math.max(...allDiscountHistory.map(d => d.discount_percentage), 0)}%
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );

  const renderDiscountsTable = () => (
    <>
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by booking number or guest name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 250 }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            size="medium"
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredDiscounts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No discounted bookings found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Once discounts are applied to bookings, they will appear here
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Booking #</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Guest Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Room</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Original Amount</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Discount %</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Discount Amount</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Final Amount</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Set By</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDiscounts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((discount) => (
                  <TableRow key={discount.id} hover>
                    <TableCell>
                      <Chip
                        label={discount.booking_number}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{discount.guest_name}</TableCell>
                    <TableCell>{discount.room_name}</TableCell>
                    <TableCell>₦{Number(discount.original_amount).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${discount.discount_percentage}%`}
                        size="small"
                        color="warning"
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'error.main' }}>
                      -₦{Number(discount.discount_amount).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="success.main">
                        ₦{Number(discount.final_amount).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={discount.set_by}>
                        <Chip
                          avatar={<Person />}
                          label={discount.set_by?.split(' ')[0]}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {new Date(discount.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Remove Discount">
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(discount)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredDiscounts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}
    </>
  );

  const renderDiscountDialog = () => (
    <Dialog open={discountDialog} onClose={() => setDiscountDialog(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Discount color="primary" />
            <Typography variant="h6">
              {globalDiscount ? 'Update Global Discount' : 'Set Global Discount'}
            </Typography>
          </Box>
          <IconButton onClick={() => setDiscountDialog(false)}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3, mt: 1 }}>
          This discount will be applied to ALL new room bookings automatically.
          {globalDiscount && ' Updating will replace the current discount.'}
        </Alert>
        
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          Discount Percentage (%)
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            type="number"
            label="Enter discount percentage"
            value={formData.discount_percentage}
            onChange={handleInputChange('discount_percentage')}
            error={!!formErrors.discount_percentage}
            helperText={formErrors.discount_percentage || 'Enter a value between 1% and 100%'}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: { min: 0, max: 100, step: 1 },
            }}
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ px: 1 }}>
            <Slider
              value={formData.discount_percentage || 0}
              onChange={handleSliderChange}
              min={0}
              max={100}
              step={1}
              marks={[
                { value: 0, label: '0%' },
                { value: 10, label: '10%' },
                { value: 20, label: '20%' },
                { value: 30, label: '30%' },
                { value: 40, label: '40%' },
                { value: 50, label: '50%' },
                { value: 60, label: '60%' },
                { value: 70, label: '70%' },
                { value: 80, label: '80%' },
                { value: 90, label: '90%' },
                { value: 100, label: '100%' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}%`}
            />
          </Box>
        </Box>
        
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Notes (Optional)"
          value={formData.notes}
          onChange={handleInputChange('notes')}
          placeholder="Add any notes about this discount..."
          sx={{ mb: 2 }}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={formData.is_active}
              onChange={handleSwitchChange}
              color="primary"
            />
          }
          label="Activate Discount Immediately"
          sx={{ mb: 2 }}
        />
        
        {formData.discount_percentage > 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Preview:</strong> A ₦100,000 booking will cost ₦
              {(100000 * (1 - formData.discount_percentage / 100)).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This discount will be applied by: {staffName} (ID: {staffId})
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDiscountDialog(false)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSetGlobalDiscount}
          disabled={submitting || !formData.discount_percentage || formData.discount_percentage <= 0}
          startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
          size="large"
        >
          {submitting ? 'Saving...' : (globalDiscount ? 'Update Discount' : 'Set Discount')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderToggleDialog = () => (
    <Dialog open={toggleDialog} onClose={() => setToggleDialog(false)} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: toggleAction === 'deactivate' ? 'warning.main' : 'success.main' }}>
          {toggleAction === 'deactivate' ? <Warning /> : <CheckCircle />}
          <Typography variant="h6">
            {toggleAction === 'deactivate' ? 'Deactivate Discount' : 'Activate Discount'}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Are you sure you want to <strong>{toggleAction === 'deactivate' ? 'deactivate' : 'activate'}</strong> this discount?
        </Typography>
        <Alert severity={toggleAction === 'deactivate' ? 'warning' : 'info'} sx={{ mt: 2 }}>
          {toggleAction === 'deactivate' 
            ? 'Deactivating will prevent this discount from being applied to new bookings.' 
            : 'Activating will make this discount available for new bookings.'}
        </Alert>
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2"><strong>Discount:</strong> {selectedDiscount?.discount_percentage}%</Typography>
          <Typography variant="body2"><strong>Set by:</strong> {selectedDiscount?.set_by}</Typography>
          <Typography variant="body2"><strong>Current Status:</strong> {selectedDiscount?.is_active ? 'Active' : 'Inactive'}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setToggleDialog(false)}>Cancel</Button>
        <ActionButton
          variant="contained"
          active={toggleAction === 'deactivate' ? 'active' : 'inactive'}
          onClick={handleToggleDiscountStatus}
          disabled={submitting}
          size="large"
        >
          {submitting ? 'Processing...' : (toggleAction === 'deactivate' ? 'DEACTIVATE' : 'ACTIVATE')}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );

  const renderDeleteDialog = () => (
    <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <Warning />
          <Typography variant="h6">Confirm Removal</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to remove this discount from booking #{selectedDiscount?.booking_number}?
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          This will restore the original booking amount for this booking only.
        </Alert>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Set by: {selectedDiscount?.set_by} on {selectedDiscount?.created_at}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleRemoveDiscount}
          disabled={submitting}
          size="large"
        >
          {submitting ? 'Removing...' : 'Remove Discount'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ========== MAIN RETURN ==========
  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          transition: 'margin 0.3s ease',
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%',
        }}
      >
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        <Paper elevation={2} sx={{ p: 3, mt: 3, backgroundColor: 'primary.main', color: 'white', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            {HOTEL_NAME}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Discount Management System
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Set global discounts for room bookings and track discount history
          </Typography>
        </Paper>

        {renderStatsCards()}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="Current Discount" />
            <Tab label="Applied Discounts" />
            <Tab label="Discount History" />
          </Tabs>
        </Box>

        <Box sx={{ mt: 3 }}>
          {activeTab === 0 && renderDiscountSettings()}
          {activeTab === 1 && renderDiscountsTable()}
          {activeTab === 2 && renderFullDiscountHistory()}
        </Box>

        <Zoom in={showScrollButton}>
          <Fab
            color="primary"
            size="small"
            onClick={scrollToTop}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1000,
            }}
          >
            <KeyboardArrowUp />
          </Fab>
        </Zoom>
      </Box>

      {renderDiscountDialog()}
      {renderToggleDialog()}
      {renderDeleteDialog()}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DiscountManagement;